import { compact, find, get, map, random, replace } from 'lodash';
import { DeepPartial } from 'redux';
import { v4 as uuid } from 'uuid';

import {
  PatientFormTemplate,
  PatientFormTemplateItem,
  PatientFormTemplateItemAnswer,
  PatientFormTemplateItemTypeKeys,
} from '../resources/types';

import { getLumaBotItemPatientInformation } from './lumaBotService';
import { updateText } from './stringsService';

export function updateSquigglies(data: PatientFormTemplate, prevData?: PatientFormTemplate) {
  if (!prevData) {
    return data;
  }
  const {
    template: { items },
  } = data;
  const {
    template: { items: prevItems },
  } = prevData;

  // Check if there are any squigglies changed
  const replacements = compact(
    map(items, (item) => {
      const prevSquiggly = get(find(prevItems, { id: item.id }), 'squiggly');
      if (prevSquiggly && prevSquiggly !== item.squiggly) {
        return {
          prev: `{{${prevSquiggly}}}`,
          next: `{{${item.squiggly}}}`,
        };
      }
    }),
  );

  return {
    ...data,
    template: {
      ...data.template,
      items: map(items, (item) => ({
        ...item,
        // Update texts with new squigglies
        title: updateText(item.title, replacements),
        description: item.description && updateText(item.description, replacements),
        locales: {
          es: {
            title: updateText(get(item, 'locales.es.title'), replacements),
            description: updateText(get(item, 'locales.es.description'), replacements),
          },
        },
      })),
    },
  };
}

export type PatientFormTemplateItemTypeConfig = {
  label: string;
  icon: string;
  isLumaIcon?: boolean;
  isAvailableOnMenu?: boolean;
  isAvailableOnLumaBotMenu?: boolean;
  isAvailableOnMenuNested?: boolean;
  isAvailableOnLumaBotMenuNested?: boolean;
  disabled?: boolean;
  titlePlaceholder?: string;
  canSelectAnswer?: boolean;
  isFinal?: boolean;
  canBeFinal?: boolean;
  isText?: boolean;
  isCCDA?: boolean;
  supportsSquiggly?: boolean;
  showPreviewButton?: boolean;
  showRedirectContent?: boolean;
  showRequiredButton?: boolean;
  showSettingsButton?: boolean;
  showSquigglyButton?: boolean;
};

// Utils
// @ts-ignore
export const ITEMS: Record<PatientFormTemplateItemTypeKeys, PatientFormTemplateItemTypeConfig> = {
  template_welcome: {
    label: 'Template Welcome',
    icon: 'play_arrow',
    isAvailableOnMenu: false,
    disabled: false,
    titlePlaceholder: 'Type welcome message here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  multiple_choice: {
    label: 'Multiple Choice',
    icon: 'check_box_outlined',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    disabled: false,
    titlePlaceholder: 'Type question here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  yes_no: {
    label: 'Yes/No',
    icon: 'tonality',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type question here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  dropdown: {
    label: 'Dropdown',
    icon: 'keyboard_arrow_down',
    isAvailableOnMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type question here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  short_text: {
    label: 'Short Text',
    icon: 'short_text',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  long_text: {
    label: 'Long Text',
    icon: 'notes',
    isAvailableOnMenu: true,
    isAvailableOnMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  email: {
    label: 'Email',
    icon: 'email',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  phone_number: {
    label: 'Phone Number',
    icon: 'phone',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  date: {
    label: 'Date',
    icon: 'today',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  address: {
    label: 'Address',
    icon: 'my_location',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  ssn: {
    label: 'SSN',
    icon: 'assignment_ind',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  zipcode: {
    label: 'Zip Code',
    icon: 'my_location',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: true,
  },
  medication_search: {
    label: 'Medication search',
    icon: 'medication',
    isLumaIcon: true,
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'What medications are you taking?',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  pharmacy_search: {
    label: 'Pharmacy search',
    icon: 'local_pharmacy',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'What is your pharmacy?',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  e_signature: {
    label: 'E-Signature',
    icon: 'edit',
    isAvailableOnMenu: true,
    isAvailableOnMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  template: {
    label: 'Template',
    icon: 'code',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnMenuNested: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: true,
    canBeFinal: true,
    isText: false,
    supportsSquiggly: false,
  },
  '2FA': {
    label: 'Two-factor authentication',
    icon: 'lock',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  patient_information: {
    label: 'Patient Information',
    icon: 'person',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  group: {
    label: 'Question Group',
    icon: 'list',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  jump: {
    label: 'Jump',
    icon: 'low_priority',
    isAvailableOnMenu: true,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  information_capture: {
    label: 'Information Capture',
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  insurance_card: {
    label: 'Insurance Card',
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  copay: {
    canBeFinal: true,
    disabled: false,
    icon: 'monetization_on',
    isAvailableOnMenu: true,
    label: 'Payment',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  docusign_templates: {
    canBeFinal: true,
    canSelectAnswer: false,
    disabled: false,
    icon: 'docusign',
    isAvailableOnMenu: true,
    isLumaIcon: true,
    isText: false,
    label: 'DocuSign',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: false,
    showSquigglyButton: false,
    supportsSquiggly: false,
    titlePlaceholder: 'Type title here...',
  },
  imh: {
    canBeFinal: true,
    canSelectAnswer: false,
    disabled: false,
    icon: 'docusign',
    isAvailableOnMenu: true,
    isLumaIcon: true,
    isText: false,
    label: 'IMH',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: false,
    showSquigglyButton: false,
    supportsSquiggly: false,
    titlePlaceholder: 'Type title here...',
  },
  insurance_verification: {
    label: 'Insurance',
    // TODO: We don't have an oficial icon yet
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  insurance_capture: {
    label: 'Insurance Capture',
    // TODO: We don't have an oficial icon yet
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: false,
  },
  // IMH related question types
  time: {
    label: 'Time',
    icon: 'short_text',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: false,
    isAvailableOnMenuNested: false,
    isAvailableOnLumaBotMenuNested: false,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: false,
  },
  year: {
    label: 'Year',
    icon: 'short_text',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: false,
    isAvailableOnMenuNested: false,
    isAvailableOnLumaBotMenuNested: false,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: false,
  },
  integer: {
    label: 'Integer',
    icon: 'short_text',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: false,
    isAvailableOnMenuNested: false,
    isAvailableOnLumaBotMenuNested: false,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: false,
  },
  bool: {
    label: 'Boolean',
    icon: 'short_text',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: false,
    isAvailableOnMenuNested: false,
    isAvailableOnLumaBotMenuNested: false,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: true,
    supportsSquiggly: false,
  },
  insurance_eligibility: {
    label: 'Eligibility',
    icon: 'price_change',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Type title here...',
    canSelectAnswer: false,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  // CCDA related questions types
  // TODO: These are placeholders for now. PRs will follow up soon.
  // This feature will only be completed after the creation of the following components:
  medication_list: {
    label: 'Medication list',
    icon: 'medication_ccda',
    isLumaIcon: true,
    isAvailableOnMenu: true,
    titlePlaceholder: 'Please list any medications you’re currently taking',
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  immunization_history: {
    icon: 'immunization_history_ccda',
    isLumaIcon: true,
    label: 'Immunization History',
    isAvailableOnMenu: true,
    titlePlaceholder: 'Immunization History',
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  allergy: {
    icon: 'allergies_ccda',
    isLumaIcon: true,
    label: 'Allergies',
    isAvailableOnMenu: true,
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  surgical_history: {
    icon: 'surgical_history_ccda',
    isLumaIcon: true,
    label: 'Surgical History',
    titlePlaceholder: 'Surgical History',
    isAvailableOnMenu: true,
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  family_history: {
    icon: 'family_history_ccda',
    isLumaIcon: true,
    label: 'Family History',
    titlePlaceholder: 'Family History',
    isAvailableOnMenu: true,
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  social_history: {
    icon: 'social_history_ccda',
    isLumaIcon: true,
    label: 'Social History',
    titlePlaceholder: 'Social History',
    isAvailableOnMenu: true,
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
  scheduler: {
    label: 'Scheduler',
    icon: 'event',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type explanation here...',
    canSelectAnswer: false,
    isFinal: true,
    canBeFinal: true,
    isText: false,
    supportsSquiggly: true,
  },
  manageAppointment: {
    label: 'Manage Appointment',
    icon: 'event_repeat',
    isAvailableOnMenu: false,
    isAvailableOnLumaBotMenu: true,
    isAvailableOnLumaBotMenuNested: true,
    disabled: false,
    titlePlaceholder: 'Type explanation here...',
    canSelectAnswer: false,
    isFinal: false,
    canBeFinal: true,
    isText: false,
    supportsSquiggly: false,
  },
  demographics: {
    icon: 'demographics_ccda',
    isLumaIcon: true,
    label: 'Demographics',
    titlePlaceholder: 'Demographics',
    isAvailableOnMenu: true,
    isCCDA: true,
    disabled: false,
    supportsSquiggly: false,
  },
};

function generateRandomIdWithPrefix(prefix: string) {
  return `${prefix ? `${prefix}-` : ''}${new Date().getTime()}${random(99999)}`;
}

export function getWelcomeScreenDefaults(): PatientFormTemplateItem {
  return {
    id: generateRandomIdWithPrefix('welcome'),
    type: 'template_welcome',
    title: 'Welcome!',
    properties: {
      template: '',
      actionLabel: 'Start',
    },
    validations: {},
  };
}

export function getAnswerDefaults(): PatientFormTemplateItemAnswer {
  return {
    id: generateRandomIdWithPrefix('answer'),
    redirectType: 'none',
  };
}

function getItemPropertiesDefaults(
  type: PatientFormTemplateItemTypeKeys,
  patientFormTemplateType: PatientFormTemplate['type'],
  isLumaBot?: boolean,
) {
  if (type === 'patient_information') {
    const patientInformationItems: PatientFormTemplateItem[] = [
      {
        ...getItemDefaults('short_text', patientFormTemplateType, isLumaBot),
        id: 'firstname',
        title: 'First Name',
        squiggly: 'patient.firstName',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('short_text', patientFormTemplateType, isLumaBot),
        id: 'lastname',
        title: 'Last Name',
        squiggly: 'patient.lastName',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('phone_number', patientFormTemplateType, isLumaBot),
        id: 'phone',
        title: 'Phone Number',
        squiggly: 'patient.phone',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('email', patientFormTemplateType, isLumaBot),
        id: 'email',
        title: 'E-mail',
        squiggly: 'patient.email',
        validations: {
          required: false,
        },
      },
      {
        ...getItemDefaults('date', patientFormTemplateType, isLumaBot),
        id: 'dateOfBirth',
        title: 'Date of Birth',
        squiggly: 'patient.dob',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('address', patientFormTemplateType, isLumaBot),
        id: 'address',
        title: 'Address',
        squiggly: 'patient.address',
        validations: {
          required: false,
        },
      },
    ];
    if (patientFormTemplateType === 'pre-qualification') {
      patientInformationItems.push({
        ...getItemDefaults('2FA', patientFormTemplateType, isLumaBot),
        title:
          'To confirm your identity, we will send you a verification code. If you entered a mobile number, you will receive the code via text message. If you entered a landline number, you will receive the code via a phone call.',
        validations: {
          required: true,
        },
      });
    }
    return {
      visibility: 'default',
      showDescription: false,
      items: patientInformationItems,
    };
  }
  if (type === 'group') {
    return {
      visibility: 'default',
      showDescription: false,
      items: [getItemDefaults('short_text', patientFormTemplateType, isLumaBot)],
    };
  }
  if (type === 'jump') {
    return {
      showDescription: false,
      script: `let jump
if (context.get('patientForm').answers['squiggly'] === 'foo') jump = 'jump1';
jump`,
      answers: [
        {
          ...getAnswerDefaults(),
          label: 'jump1',
        },
      ],
    };
  }
  if (type === 'docusign_templates') {
    return {
      showDescription: true,
    };
  }

  if (type === 'yes_no') {
    return {
      answers: [
        {
          ...getAnswerDefaults(),
          label: 'Yes',
          locales: { es: { label: 'Sí' }, pt: { label: 'Sim' } },
          disabled: true,
        },
        {
          ...getAnswerDefaults(),
          label: 'No',
          locales: { es: { label: 'No' }, pt: { label: 'Não' } },
          disabled: true,
        },
      ],
    };
  }

  if (type === 'family_history' || type === 'immunization_history' || type === 'surgical_history') {
    return {
      [`${replace(type, '_h', 'H')}`]: [
        {
          id: uuid(),
        },
      ],
    };
  }
  if (type === 'social_history') {
    const socialHistoryItems: PatientFormTemplateItem[] = [
      {
        ...getItemDefaults('dropdown', patientFormTemplateType, isLumaBot),
        id: 'maritalStatus',
        title: 'Marital Status',
        text: 'Marital Status',
        squiggly: 'patient.maritalStatus',
        properties: {
          visibility: 'default',
          answers: [
            { ...getAnswerDefaults(), label: 'Single', code: 'S', disabled: true },
            { ...getAnswerDefaults(), label: 'Married', code: 'M', disabled: true },
            { ...getAnswerDefaults(), label: 'Divorced', code: 'D', disabled: true },
            { ...getAnswerDefaults(), label: 'Domestic Partner', code: 'T', disabled: true },
            { ...getAnswerDefaults(), label: 'Separated', code: 'L', disabled: true },
            { ...getAnswerDefaults(), label: 'Widowed', code: 'W', disabled: true },
            { ...getAnswerDefaults(), label: 'Annulled', code: 'A', disabled: true },
            { ...getAnswerDefaults(), label: 'Polygamous', code: 'P', disabled: true },
          ],
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType, isLumaBot),
        id: 'howMuchSmoke',
        title: 'How much do you smoke?',
        text: 'How much do you smoke?',
        squiggly: 'patient.howMuchSmoke',
        properties: {
          visibility: 'default',
          answers: [
            { ...getAnswerDefaults(), label: 'I smoke every day', code: '449868002', disabled: true },
            { ...getAnswerDefaults(), label: 'I smoke some days', code: '428041000124106', disabled: true },
            { ...getAnswerDefaults(), label: "I used to smoke, but I don't anymore", code: '8517006', disabled: true },
            { ...getAnswerDefaults(), label: 'I never smoked', code: '266919005', disabled: true },
            { ...getAnswerDefaults(), label: "I don't know if I smoked or not", code: '266927001', disabled: true },
            { ...getAnswerDefaults(), label: "I'm a heavy smoker", code: '428071000124103', disabled: true },
            { ...getAnswerDefaults(), label: "I'm a light smoker", code: '428061000124105', disabled: true },
          ],
        },
      },
      {
        ...getItemDefaults('short_text', patientFormTemplateType, isLumaBot),
        id: 'alcoholicDrinks',
        title: 'How many alcoholic drinks do you drink per day?',
        text: 'How many alcoholic drinks do you drink per day?',
        squiggly: 'patient.alcoholicDrinks',
        properties: {
          visibility: 'default',
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType, isLumaBot),
        id: 'recreationalDrugs',
        title: 'Do you use recreational drugs?',
        text: 'Do you use recreational drugs?',
        squiggly: 'patient.recreationalDrugs',
        properties: {
          visibility: 'default',
          answers: [
            { ...getAnswerDefaults(), label: 'Yes', disabled: true },
            { ...getAnswerDefaults(), label: 'No', disabled: true },
          ],
        },
      },
      {
        ...getItemDefaults('short_text', patientFormTemplateType, isLumaBot),
        id: 'exerciseHours',
        title: 'How many hours do you exercise per week?',
        text: 'How many hours do you exercise per week?',
        squiggly: 'patient.exerciseHours',
        properties: {
          visibility: 'default',
        },
      },
    ];

    return {
      showDescription: false,
      visibility: 'default',
      items: socialHistoryItems,
    };
  }

  if (type === 'demographics') {
    const demographicItems: PatientFormTemplateItem[] = [
      {
        ...getItemDefaults('short_text', patientFormTemplateType),
        id: 'firstname',
        title: 'First Name',
        squiggly: 'patient.firstname',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('short_text', patientFormTemplateType),
        id: 'lastname',
        title: 'Last Name',
        squiggly: 'patient.lastname',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('short_text', patientFormTemplateType),
        id: 'middlename',
        title: 'Middle Name',
        squiggly: 'patient.middlename',
        properties: {
          visibility: 'default',
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType),
        id: 'suffix',
        title: 'Suffix',
        squiggly: 'patient.suffix',
        properties: {
          visibility: 'default',
          answers: [
            { ...getAnswerDefaults(), label: 'II', code: 'II', disabled: true },
            { ...getAnswerDefaults(), label: 'III', code: 'III', disabled: true },
            { ...getAnswerDefaults(), label: 'IV', code: 'IV', disabled: true },
            { ...getAnswerDefaults(), label: 'Jr.', code: 'Jr.', disabled: true },
            { ...getAnswerDefaults(), label: 'Sr.', code: 'Sr.', disabled: true },
          ],
        },
      },
      {
        ...getItemDefaults('date', patientFormTemplateType),
        id: 'dateOfBirth',
        title: 'Date of Birth',
        squiggly: 'patient.dob',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('address', patientFormTemplateType),
        id: 'currentAddress',
        title: 'Current Address',
        squiggly: 'patient.currentAddress',
        properties: {
          visibility: 'default',
        },
      },
      {
        ...getItemDefaults('address', patientFormTemplateType),
        id: 'previousAddress',
        title: 'Previous Address',
        squiggly: 'patient.previousAddress',
        properties: {
          visibility: 'default',
        },
      },
      {
        ...getItemDefaults('phone_number', patientFormTemplateType),
        id: 'phone',
        title: 'Phone Number',
        squiggly: 'patient.phone',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('email', patientFormTemplateType),
        id: 'email',
        title: 'Email',
        squiggly: 'patient.email',
        properties: {
          visibility: 'default',
          mustExist: true,
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType),
        id: 'race',
        title: 'Race',
        squiggly: 'patient.race',
        properties: {
          visibility: 'default',
          mustExist: true,
          answers: [
            { ...getAnswerDefaults(), label: 'White', code: 'white', disabled: true },
            {
              ...getAnswerDefaults(),
              label: 'Black or African American',
              code: 'black-or-african-american',
              disabled: true,
            },
            {
              ...getAnswerDefaults(),
              label: 'American Indian or Alaska Native',
              code: 'american-indian-or-alaska-native',
              disabled: true,
            },
            { ...getAnswerDefaults(), label: 'Asian', code: 'asian', disabled: true },
            {
              ...getAnswerDefaults(),
              label: 'Native Hawaiian or Other Pacific Islander',
              code: 'native-hawaiian-or-other-pacific-islander',
              disabled: true,
            },
            {
              ...getAnswerDefaults(),
              label: 'Other',
              code: 'other-race',
              disabled: true,
            },
          ],
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType),
        id: 'ethnicityGroup',
        title: 'Ethnicity',
        squiggly: 'patient.ethnicityGroup',
        properties: {
          visibility: 'default',
          mustExist: true,
          answers: [
            { ...getAnswerDefaults(), label: 'Hispanic or Latino', code: 'hispanic-or-latino', disabled: true },
            {
              ...getAnswerDefaults(),
              label: 'Not Hispanic or Latino',
              code: 'not-hispanic-or-latino',
              disabled: true,
            },
          ],
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType),
        id: 'birthSex',
        title: 'Sex (Assigned at Birth)',
        squiggly: 'patient.birthSex',
        properties: {
          visibility: 'default',
          mustExist: true,
          answers: [
            { ...getAnswerDefaults(), label: 'Male', code: 'M', disabled: true },
            {
              ...getAnswerDefaults(),
              label: 'Female',
              code: 'F',
              disabled: true,
            },
            {
              ...getAnswerDefaults(),
              label: 'Unknown',
              code: 'UNK',
              disabled: true,
            },
          ],
        },
        validations: {
          required: true,
        },
      },
      {
        ...getItemDefaults('dropdown', patientFormTemplateType),
        id: 'language',
        title: 'Preferred Language',
        squiggly: 'patient.language',
        properties: {
          visibility: 'default',
          mustExist: true,
          answers: [
            { ...getAnswerDefaults(), label: 'English', code: 'en', disabled: true },
            { ...getAnswerDefaults(), label: 'Spanish', code: 'es', disabled: true },
            { ...getAnswerDefaults(), label: 'Arabic', code: 'ar', disabled: true },
            { ...getAnswerDefaults(), label: 'Armenian', code: 'hy', disabled: true },
            { ...getAnswerDefaults(), label: 'Bengali', code: 'bn', disabled: true },
            { ...getAnswerDefaults(), label: 'Burmese', code: 'my', disabled: true },
            { ...getAnswerDefaults(), label: 'Chinese', code: 'zh', disabled: true },
            { ...getAnswerDefaults(), label: 'French', code: 'fr', disabled: true },
            { ...getAnswerDefaults(), label: 'German', code: 'de', disabled: true },
            { ...getAnswerDefaults(), label: 'Greek', code: 'gr', disabled: true },
            { ...getAnswerDefaults(), label: 'Gujarati', code: 'gu', disabled: true },
            { ...getAnswerDefaults(), label: 'Haitian Creole', code: 'ht', disabled: true },
            { ...getAnswerDefaults(), label: 'Hindi', code: 'hi', disabled: true },
            { ...getAnswerDefaults(), label: 'Italian', code: 'it', disabled: true },
            { ...getAnswerDefaults(), label: 'Korean', code: 'ko', disabled: true },
            { ...getAnswerDefaults(), label: 'Marathi', code: 'mr', disabled: true },
            { ...getAnswerDefaults(), label: 'Polish', code: 'pl', disabled: true },
            { ...getAnswerDefaults(), label: 'Portuguese', code: 'pt', disabled: true },
            { ...getAnswerDefaults(), label: 'Punjabi', code: 'pa', disabled: true },
            { ...getAnswerDefaults(), label: 'Russian', code: 'ru', disabled: true },
            { ...getAnswerDefaults(), label: 'Urdu', code: 'ur', disabled: true },
            { ...getAnswerDefaults(), label: 'Vietnamese', code: 'vi', disabled: true },
          ],
        },
        validations: {
          required: true,
        },
      },
    ];

    return {
      showDescription: false,
      visibility: 'default',
      items: demographicItems,
    };
  }

  if (type === 'insurance_verification') {
    return {
      canSkip: false,
      showDescription: false,
      visibility: 'default',
      showCustomAction: false,
      scoringEnabled: false,
      addToAppointment: false,
    };
  }

  if (type === 'insurance_capture') {
    return {
      canSkip: false,
      showDescription: false,
      visibility: 'default',
      showCustomAction: false,
      scoringEnabled: false,
      addToAppointment: false,
    };
  }

  if (type === 'scheduler') {
    return {
      schedulerButton: 'Open Scheduler',
      locales: { es: { schedulerButton: 'Abre el Gestor de Citas' }, pt: { schedulerButton: 'Abrir Agenda' } },
    };
  }

  // TODO: adjust different properties per type so we can cleanup the config on type change
  const { canSelectAnswer } = ITEMS[type];
  return {
    showDescription: false,
    visibility: 'default',
    showCustomAction: false,
    scoringEnabled: false,
    addToAppointment: false,
    allowMultipleSelection: canSelectAnswer ? false : undefined,
    allowOtherChoice: canSelectAnswer ? false : undefined,
    includeNoneOfAboveChoice: canSelectAnswer ? false : undefined,
    answers: canSelectAnswer ? [getAnswerDefaults()] : undefined,
  };
}

// TODO: Move methods to get defaults to service
export function getItemDefaults(
  type: PatientFormTemplateItemTypeKeys,
  patientFormTemplateType: PatientFormTemplate['type'],
  isLumaBot?: boolean,
): PatientFormTemplateItem {
  const id = generateRandomIdWithPrefix('item');
  let title = '';

  if (isLumaBot && type === 'patient_information') return getLumaBotItemPatientInformation();

  switch (type) {
    case 'patient_information':
      title = 'Patient Information';
      break;
    case 'copay':
      title = 'Your copay for this appointment is:';
      break;
    case 'medication_search':
      title = 'What medications are you taking?';
      break;
    case 'pharmacy_search':
      title = 'What is your pharmacy?';
      break;
    case 'jump':
      title = 'Jump';
      break;
    // CCDA Items
    case 'medication_list':
      title = 'Please list any medications you’re currently taking';
      break;
    case 'immunization_history':
      title = 'Immunization History';
      break;
    case 'surgical_history':
      title = 'Surgical History';
      break;
    case 'family_history':
      title = 'Family History';
      break;
    case 'allergy':
      title = 'Please search for your allergy';
      break;
    case 'social_history':
      title = 'Social History';
      break;
    case 'insurance_eligibility':
      title = 'Insurance Eligibility';
      break;
    case 'insurance_verification':
      title = 'Insurance Information';
      break;
    case 'insurance_capture':
      title = 'Insurance Capture';
      break;
    case 'demographics':
      title = 'Demographics';
      break;
    default:
  }

  return {
    id,
    type,
    squiggly: id,
    title,
    redirectType: ITEMS[type].isFinal ? 'isFinal' : 'none',
    properties: getItemPropertiesDefaults(type, patientFormTemplateType, isLumaBot),
    validations: {
      required: false,
    },
  };
}

export function getDefaults(isLumaBot: boolean = false): DeepPartial<PatientFormTemplate> {
  return {
    title: '',
    active: true,
    sendToDirectMessageEmail: false,
    type: 'intake',
    reminder: {
      enabled: false,
    },
    insurance: {
      enabled: false,
    },
    externalId: {
      source: 'luma',
    },
    template: {
      items: [getItemDefaults('multiple_choice', 'intake', isLumaBot)],
      welcomeScreens: [getWelcomeScreenDefaults()],
      // version: '0.1.0', // initial version
      // version: '0.2.0', // template.welcomeScreens
      // version: '0.2.1', // item.properties.addToAppointment
      // version: '0.3.0', // fix typo `wellcome > welcome` + add more item types + required + description
      // version: '0.4.0', // type: 'intake|pre-qualification'
      // version: '0.5.0', // item type email + item.properties.showCustomAction + required is false by default
      // version: '0.6.0', // item.squiggly
      // version: '0.7.0', // item.redirectType + item.properties.goTo
      // version: 'missed', // new item types: 'medication_search', 'pharmacy_search'
      // version: '0.8.0', // new item types: 'medication_search', 'pharmacy_search', 'group'
      // version: '0.9.0', // new item type: 'jump'
      // version: '0.10.0', // new item type: 'patient_information' with '2FA' nested
      // version: '0.11.0', // new item.redirectType: 'isFinal'
      // version: '0.12.0', // new item type: 'address'
      // version: '0.13.0', // new item type: 'payment'
      version: '0.14.0', // new props for scoring
    },
  };
}
